/**
 * The shared DS 2.0 focus ring.
 *
 * Tailwind must see every complete class name at build time, so the selector variants live in this
 * static map rather than being assembled from string fragments. All variants draw the same effect:
 * the opaque inner gap at the library's 2px spread, over the translucent outer halo at 4px.
 */
const focusRingClasses = {
  /** A control that owns its `:focus-visible` state. */
  self: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** Checkbox focus lives on its parent control while the visual ring belongs to the inner box. */
  checkbox: [
    'group-focus-visible/cb:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** Accordion focus lives on the trigger while the shared ring surrounds the whole item. */
  accordionItem: [
    '[&:has(>[data-part=header]>[data-part=trigger]:focus-visible)]:z-[1]',
    '[&:has(>[data-part=header]>[data-part=trigger]:focus-visible)]:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** Menu rows use a panel-aware inner gap so it follows the control surface in both themes. */
  menuItem: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--ds-menu-focus-gap),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--ds-menu-focus)]',
  ].join(' '),

  /** Select rows use the same panel-aware gap through their own slot family. */
  selectItem: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--ds-select-item-focus-gap),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--ds-select-item-focus)]',
  ].join(' '),
} as const;

export type FocusRingTarget = keyof typeof focusRingClasses;

export const focusRing = (target: FocusRingTarget = 'self') => focusRingClasses[target];
